import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BrandMark } from "../components/Brand";
import { api, formatApiError } from "../lib/api";
import { saveAdmin } from "../lib/auth";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Clear any stale auth state when this page mounts so an old (mock) localStorage
  // payload can't trick a freshly logged-in user into a redirect loop.
  useEffect(() => {
    try { localStorage.removeItem("instabridge_admin"); } catch (err) { console.error("clear-admin-state", err); }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveAdmin(data);
      toast.success(`Welcome, ${data.user.name}`);
      navigate("/admin");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between bg-[#0A2540] text-white p-12 relative overflow-hidden">
        <BrandMark inverted />
        <div className="relative z-10">
          <div className="uppercase-label text-white/60 mb-4">Underwriter portal</div>
          <h2 className="font-display text-4xl xl:text-5xl font-light tracking-tight leading-tight">
            Decide cases<br /><span className="font-semibold">in minutes,</span> not weeks.
          </h2>
          <p className="mt-6 text-white/70 max-w-md leading-relaxed">
            A unified pipeline for your entire underwriting team. Search, decide, fund — without leaving the dashboard.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-white/80">
            {["Bcrypt-hashed passwords + JWT sessions","15-min lockout after 5 failed attempts","HttpOnly secure cookies","SOC-2 ready audit trail"].map((x) => (
              <li key={x} className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#0066FF]" /> {x}</li>
            ))}
          </ul>
        </div>
        <div className="text-xs text-white/50">© {new Date().getFullYear()} Instabridge Funding · Internal portal</div>
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-[#0066FF]/30 blur-3xl" />
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div className="lg:hidden mb-8"><BrandMark /></div>
          <div>
            <h1 className="font-display text-3xl text-[#0A2540] font-semibold tracking-tight">Underwriter sign in</h1>
            <p className="text-[#6B7280] mt-1.5 text-sm">Use your Instabridge admin credentials. Looking for the attorney portal? <Link to="/attorney-login" className="text-[#0066FF] hover:underline">Click here</Link>.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@instabridgefunding.com" className="mt-1.5" data-testid="login-email" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="login-password" required />
            </div>
          </div>
          {error && <div className="text-sm text-[#B91C1C] bg-red-50 border border-red-100 px-3 py-2 rounded-md" data-testid="login-error">{error}</div>}
          <Button type="submit" className="w-full btn-primary-navy h-11 rounded-lg" disabled={loading} data-testid="login-submit-btn">
            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in…</>) : "Sign in to dashboard"}
          </Button>
          <div className="text-center text-sm text-[#6B7280]">
            <Link to="/" className="hover:text-[#0A2540]" data-testid="login-back-home">← Back to homepage</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
